import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import api from '../../api/apiClient';
import { useAppSelector } from '../../hooks/useRedux';
import { useUI } from '../../context/UIContext';
import '../../styles/AddOnTemplatePage.css';

// Define the category type
type Category = {
  id: string;
  name: string;
};

interface AddOnTemplate {
  _id: string;
  name: string;
  description: string;
  category: string;
  suggestedPrice: {
    min: number;
    max: number;
  };
  suggestedDuration: number;
}

// Stylist's added add-on from API (customized name/price/duration)
interface MyAddOn {
  _id: string;
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
  isActive?: boolean;
  displayOrder?: number;
}

// List of available categories
const CATEGORIES: Category[] = [
  { id: 'hair', name: 'Hair' },
  { id: 'braiding', name: 'Braiding' },
  { id: 'barber', name: 'Barber' },
  { id: 'nails', name: 'Nails' },
  { id: 'makeup', name: 'Makeup' },
  { id: 'skincare', name: 'Skincare' },
  { id: 'lashes', name: 'Lashes' },
  { id: 'brows', name: 'Brows' },
  { id: 'waxing', name: 'Waxing' },
  { id: 'massage', name: 'Massage' },
  { id: 'wellness', name: 'Wellness' },
  { id: 'fitness', name: 'Fitness' },
  { id: 'therapy', name: 'Therapy' }
];

// Validation constants
const MAX_NAME_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_PRICE = 1000;
const MAX_DURATION = 480; // 8 hours

const AddOnTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useUI();
  const auth = useAppSelector(state => state.auth);
  const userId = auth.user?.id || auth.user?._id;
  
  // Filter categories based on stylist's specialties
  const stylistSpecialties = auth.user?.stylistProfile?.specialties || [];
  const filteredCategories = stylistSpecialties.length > 0
    ? CATEGORIES.filter(cat => stylistSpecialties.includes(cat.id))
    : CATEGORIES; // Show all if no specialties (fallback)
  
  // State for selected category, templates data, loading and error messages
  const [selectedCategory, setSelectedCategory] = useState<string>(
    filteredCategories.length > 0 ? filteredCategories[0].id : CATEGORIES[0].id
  );
  const [templates, setTemplates] = useState<AddOnTemplate[]>([]);
  const [myAddOnsList, setMyAddOnsList] = useState<MyAddOn[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AddOnTemplate | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedPrice, setEditedPrice] = useState('');
  const [editedDuration, setEditedDuration] = useState('');
  
  // Check if the user is logged in and is a stylist
  useEffect(() => {
    if (!auth.token) {
      navigate('/login');
    } else if (auth.user && auth.user.userType !== 'stylist') {
      navigate('/dashboard');
    }
  }, [auth, navigate]);

  // Fetch my existing add-ons (full objects so we can show customized name/price/duration)
  const fetchMyAddOns = async () => {
    try {
      const response = await api.get(`/stylists/${userId}/add-ons`);
      const list: MyAddOn[] = (response.data.data || []).map((addon: any) => ({
        _id: addon._id,
        name: addon.name,
        description: addon.description,
        priceCents: addon.priceCents,
        durationMinutes: addon.durationMinutes ?? 0,
        isActive: addon.isActive,
        displayOrder: addon.displayOrder,
      }));
      setMyAddOnsList(list);
    } catch (error: any) {
      console.error('Error fetching my add-ons:', error);
    }
  };

  // Fetch templates from the API when the selected category changes
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching add-on templates for category:', selectedCategory);
      const response = await api.get(`/add-on-templates/category/${selectedCategory}`);
      const templatesFromApi = response.data.data || [];
      console.log('Templates received:', templatesFromApi);
      
      setTemplates(templatesFromApi);
    } catch (err: any) {
      console.error('Error fetching add-on templates:', err);
      setError(err.message || 'Error fetching add-on templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchMyAddOns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Handle category selection change
  const handleCategorySelect = (id: string) => {
    setSelectedCategory(id);
  };

  // Open edit modal – ensure all fields are defined for controlled inputs
  const handleEditBeforeAdding = (template: AddOnTemplate) => {
    setEditingTemplate(template);
    setEditedName(template.name || '');
    setEditedDescription(template.description ?? '');
    const priceMin = typeof template.suggestedPrice?.min === 'number' ? template.suggestedPrice.min : 0;
    setEditedPrice((priceMin / 100).toString());
    const duration = typeof template.suggestedDuration === 'number' ? template.suggestedDuration : 0;
    setEditedDuration(duration.toString());
    setShowEditModal(true);
  };

  // Save edited add-on
  const handleSaveEditedAddOn = async () => {
    if (!editingTemplate) return;
    if (!userId) {
      showSnackbar('Please sign in again to add add-ons.', 'error');
      return;
    }

    // Comprehensive validation
    const trimmedName = editedName.trim();
    const trimmedDescription = editedDescription.trim();
    
    if (!trimmedName) {
      showSnackbar('Please enter a name', 'error');
      return;
    }
    
    if (trimmedName.length > MAX_NAME_LENGTH) {
      showSnackbar(`Name must be ${MAX_NAME_LENGTH} characters or less`, 'error');
      return;
    }
    
    if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
      showSnackbar(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`, 'error');
      return;
    }
    
    const price = parseFloat(editedPrice);
    const duration = parseInt(editedDuration);
    
    if (isNaN(price) || price < 0) {
      showSnackbar('Please enter a valid price', 'error');
      return;
    }
    
    if (price > MAX_PRICE) {
      showSnackbar(`Price cannot exceed $${MAX_PRICE}`, 'error');
      return;
    }
    
    if (isNaN(duration) || duration < 0) {
      showSnackbar('Please enter a valid duration', 'error');
      return;
    }
    
    if (duration > MAX_DURATION) {
      showSnackbar(`Duration cannot exceed ${MAX_DURATION} minutes`, 'error');
      return;
    }

    const normalizedIncomingName = trimmedName.toLowerCase().replace(/\s+/g, ' ');
    const duplicateLocal = myAddOnsList.some(
      (addon) => addon.name.toLowerCase().replace(/\s+/g, ' ') === normalizedIncomingName
    );
    if (duplicateLocal) {
      showSnackbar(`Add-on "${trimmedName}" already exists.`, 'error');
      return;
    }
    
    try {
      setAdding(editingTemplate._id);
      setShowEditModal(false);
      
      const response = await api.post(`/stylists/${userId}/add-ons`, {
        name: trimmedName,
        description: trimmedDescription,
        priceCents: Math.round(price * 100),
        durationMinutes: duration,
        category: editingTemplate.category,
      });
      
      await fetchMyAddOns();
      showSnackbar(
        response.data.message || `"${trimmedName}" has been added to your services!`,
        'success'
      );
    } catch (error: any) {
      console.error('[AddOnTemplate] Error adding add-on:', error);
      
      // Handle validation errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        showSnackbar(error.response.data.errors.join(', '), 'error');
      } else {
        showSnackbar(
          error.response?.data?.message || 'Failed to add add-on. Please try again.',
          'error'
        );
      }
    } finally {
      setAdding(null);
      setEditingTemplate(null);
    }
  };

  // Remove add-on (use actual add-on name for customized names)
  const handleRemoveAddOn = async (template: AddOnTemplate) => {
    const nameToRemove = getAddedNameForTemplate(template);
    if (!nameToRemove) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove this add-on from all your services? This cannot be undone.`)) {
      return;
    }
    
    try {
      setRemoving(template._id);
      const response = await api.delete(`/stylists/${userId}/add-ons/${encodeURIComponent(nameToRemove)}`);
      await fetchMyAddOns();
      showSnackbar(
        response.data.message || `"${nameToRemove}" has been removed from your services!`,
        'success'
      );
    } catch (error: any) {
      console.error('[AddOnTemplate] Error removing add-on:', error);
      showSnackbar(error.response?.data?.message || 'Failed to remove add-on. Please try again.', 'error');
    } finally {
      setRemoving(null);
    }
  };

  // Match added add-on to template (exact or customized name)
  const getAddedAddOnForTemplate = (template: AddOnTemplate): MyAddOn | null => {
    const t = template.name.toLowerCase().trim();
    return myAddOnsList.find(
      (addon) => {
        const n = addon.name.toLowerCase().trim();
        return n === t || (n.startsWith(t) && n.length > t.length);
      }
    ) ?? null;
  };

  const isAddOnAdded = (template: AddOnTemplate): boolean => !!getAddedAddOnForTemplate(template);

  const getAddedNameForTemplate = (template: AddOnTemplate): string | null =>
    getAddedAddOnForTemplate(template)?.name ?? null;

  // Show message if no templates available for selected specialties
  if (filteredCategories.length === 0 && stylistSpecialties.length > 0) {
    return (
      <div className="addon-template-page">
        <div className="tesla-header">
          <h1 className="tesla-title">Add-On Templates</h1>
          <p className="tesla-subtitle">No templates available for your specialties</p>
        </div>
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
            </svg>
          </div>
          <h3>No Templates Available</h3>
          <p>No add-on templates match your selected specialties. Please update your profile settings.</p>
          <button 
            className="tesla-button-primary" 
            onClick={() => navigate('/stylist/profile')}
          >
            Update Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="addon-template-page">
      {/* Header Section - Tesla-inspired */}
      <div className="tesla-header">
        <h1 className="tesla-title">Add-On Templates</h1>
        <p className="tesla-subtitle">Browse and add complementary services to enhance your offerings</p>
      </div>

      {/* Category Navigation - Horizontal Scrollable */}
      <div className="category-nav-container">
        <div className="category-nav">
          {filteredCategories.map((category) => (
            <button
              key={category.id}
              className={`category-button ${
                selectedCategory === category.id ? 'category-button-active' : ''
              }`}
              onClick={() => handleCategorySelect(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Templates Grid */}
      <div className="templates-grid">
        {loading ? (
          <div className="tesla-loader">
            <div className="tesla-loader-spinner"></div>
            <p>Loading add-on templates...</p>
          </div>
        ) : error ? (
          <div className="tesla-error">
            <p>{error}</p>
            <button className="tesla-button-secondary" onClick={fetchTemplates}>
              Try Again
            </button>
          </div>
        ) : templates.length > 0 ? (
          templates.map((template) => {
            const isAdded = isAddOnAdded(template);
            const isAdding = adding === template._id;
            const addedAddOn = getAddedAddOnForTemplate(template);
            const displayName = addedAddOn?.name ?? template.name;
            const displayPrice = addedAddOn != null
              ? formatCurrency(addedAddOn.priceCents / 100)
              : `${formatCurrency(template.suggestedPrice.min / 100)} - ${formatCurrency(template.suggestedPrice.max / 100)}`;
            const displayDuration = addedAddOn?.durationMinutes ?? template.suggestedDuration;
            const displayDescription = addedAddOn?.description ?? template.description;
            
            return (
              <div key={template._id} className={`template-card ${isAdded ? 'template-card-added' : ''}`}>
                <div className="template-card-content">
                  <div className="template-header">
                    <h2 className="template-name">{displayName}</h2>
                    {isAdded && (
                      <span className="added-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                        </svg>
                        Added
                      </span>
                    )}
                  </div>
                  
                  <div className="template-price-tag">
                    <span className="template-price-label">{addedAddOn ? 'Your Price' : 'Suggested Price'}</span>
                    <span className="template-price-value">
                      {displayPrice}
                    </span>
                  </div>
                  
                  {(displayDescription ?? '') && (
                    <div className="template-description">
                      {displayDescription}
                    </div>
                  )}
                  
                  <div className="template-specs">
                    <div className="template-spec">
                      <span className="template-spec-label">Duration</span>
                      <span className="template-spec-value">{displayDuration} mins</span>
                    </div>
                    
                    <div className="template-spec">
                      <span className="template-spec-label">Category</span>
                      <span className="template-spec-value">{template.category}</span>
                    </div>
                  </div>
                </div>
                
                <div className="template-card-footer">
                  {isAdded ? (
                    <button
                      className="tesla-button-remove"
                      onClick={() => handleRemoveAddOn(template)}
                      disabled={removing === template._id}
                    >
                      {removing === template._id ? 'Removing...' : 'Remove from Services'}
                    </button>
                  ) : (
                    <button
                      className="tesla-button-primary"
                      onClick={() => handleEditBeforeAdding(template)}
                      disabled={isAdding}
                    >
                      {isAdding ? 'Adding...' : 'Customize & Add'}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
              </svg>
            </div>
            <h3>No Templates Available</h3>
            <p>No add-on templates found for the selected category.</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingTemplate && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customize Add-On</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="input-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Add-on name"
                />
              </div>

              <div className="input-group">
                <label>Description</label>
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  placeholder="Add-on description"
                  rows={3}
                />
              </div>

              <div className="input-row">
                <div className="input-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editedPrice}
                    onChange={(e) => setEditedPrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="input-group">
                  <label>Duration (mins)</label>
                  <input
                    type="number"
                    min="0"
                    value={editedDuration}
                    onChange={(e) => setEditedDuration(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="tesla-button-secondary" 
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="tesla-button-primary" 
                onClick={handleSaveEditedAddOn}
              >
                Add to Services
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnTemplatePage;
